/**
 * Stamp invoice values onto an uploaded PDF template using the positions placed
 * in the template field editor. Mirrors the editor's coordinate model:
 * PlacedField.{x,y} are pdf-lib coords (bottom-left origin) of the box's bottom
 * edge, width/height in PDF points, 0-based page index.
 *
 * `whiteout` paints an opaque box under each value first, so a template that
 * still shows its [tokens] comes out clean (and a blank template is unaffected).
 */
import { PDFDocument, StandardFonts, rgb, type PDFFont } from "pdf-lib";
import type { PlacedField } from "@/components/admin/TemplateFieldEditor";

/**
 * Which placed fields are stamped bold: TOTAL_AMOUNT — the Gesamtbetrag the
 * owner has to pay, so it must stand out regardless of the uploaded template's
 * own styling (the whiteout box wipes that styling anyway) — and PROPERTY_ADDRESS.
 * Every placement of either field is bolded; all other fields (net, VAT, summary
 * rows) stay regular.
 */
const BOLD_FIELD_NAMES = new Set(["TOTAL_AMOUNT", "PROPERTY_ADDRESS"]);

export function boldFieldSet<T extends { name: string }>(fields: T[]): Set<T> {
  const bold = new Set<T>();
  for (const f of fields) if (BOLD_FIELD_NAMES.has(f.name)) bold.add(f);
  return bold;
}

/**
 * Largest font size ≤ `preferred` at which `text` fits within `maxWidth`, down
 * to a 5pt floor (keeps overrun off the template's pre-printed text).
 */
function fitFontSize(
  font: PDFFont,
  text: string,
  preferred: number,
  maxWidth: number
): number {
  if (maxWidth <= 0) return preferred;
  let size = preferred;
  while (size > 5 && font.widthOfTextAtSize(text, size) > maxWidth) {
    size -= 0.5;
  }
  return size;
}

export async function stampInvoicePdf(
  templateBytes: Uint8Array,
  fields: PlacedField[],
  values: Record<string, string>,
  opts: { whiteout?: boolean } = {}
): Promise<Uint8Array> {
  const whiteout = opts.whiteout ?? true;
  const pdf = await PDFDocument.load(templateBytes);
  const font = await pdf.embedFont(StandardFonts.Helvetica);
  // Bold face embedded lazily — a template with no bold field carries no unused
  // font. `bold` holds the exact placements to render bold (see boldFieldSet).
  let boldFont: PDFFont | null = null;
  const bold = boldFieldSet(fields);
  const pages = pdf.getPages();

  for (const f of fields) {
    const value = values[f.name];
    if (value == null || value === "") continue;
    const page = pages[f.page];
    if (!page) continue;

    if (whiteout) {
      page.drawRectangle({
        x: f.x,
        y: f.y,
        width: f.width,
        height: f.height,
        color: rgb(1, 1, 1),
      });
    }

    const text = sanitize(value);
    const fieldFont = bold.has(f)
      ? (boldFont ??= await pdf.embedFont(StandardFonts.HelveticaBold))
      : font;
    // Safety net: sanitize() already folds non-WinAnsi glyphs, but never let one
    // unexpected field throw (widthOfTextAtSize/drawText both encode) and abort
    // the whole stamped PDF — skip just that field, leaving its whiteout blank.
    try {
      // Shrink-to-fit: a long owner name / property address must not overrun the
      // whiteout box and collide with the template's pre-printed text.
      const size = fitFontSize(fieldFont, text, f.fontSize || 10, f.width - 4);
      // Vertically center the baseline within the box.
      const baselineY = f.y + Math.max(0, (f.height - size) / 2) + size * 0.15;
      page.drawText(text, {
        x: f.x + 2,
        y: baselineY,
        size,
        font: fieldFont,
        color: rgb(0.12, 0.16, 0.2),
      });
    } catch {
      // leave the whiteout box blank for this field
    }
  }

  return pdf.save();
}

/**
 * Helvetica's WinAnsi (CP1252) encoding covers Latin-1 (incl. German umlauts/
 * ss) plus typographic punctuation Windows maps into 0x80-0x9F (en-dash, euro,
 * smart quotes...). pdf-lib THROWS on anything outside it -- Latin-Extended
 * letters in real owner names (Ł ř İ ą č) and any
 * non-Latin script (Cyrillic/Greek/CJK). Fold the input so a foreign name can
 * never crash the whole stamp:
 *   1. normalize exotic dashes/spaces/quotes to WinAnsi equivalents,
 *   2. keep Latin-1 and the WinAnsi high-range punctuation untouched,
 *   3. transliterate the common non-decomposing Latin letters,
 *   4. NFKD-fold accented letters to their base (ř->r, č->c),
 *   5. drop whatever still isn't WinAnsi-encodable (Cyrillic/CJK -> removed).
 */
const WINANSI_HIGH =
  "\u20ac\u201a\u0192\u201e\u2026\u2020\u2021\u02c6\u2030\u0160\u2039\u0152\u017d\u2018\u2019\u201c\u201d\u2022\u2013\u2014\u02dc\u2122\u0161\u203a\u0153\u017e\u0178";
const TRANSLITERATE: Record<string, string> = {
  Ł: "L",
  ł: "l",
  Đ: "D",
  đ: "d",
  İ: "I",
  ı: "i",
  ẞ: "SS",
  Ħ: "H",
  ħ: "h",
  Ŧ: "T",
  ŧ: "t",
};
export function sanitize(s: string): string {
  const normalized = s
    .replace(/[\u2012\u2014\u2015]/g, "\u2013") // exotic dashes -> en dash
    .replace(/[\u00a0\u2002\u2007\u2009\u200a\u202f]/g, " ") // exotic spaces
    .replace(/[\u2018\u2019]/g, "'") // curly single quotes
    .replace(/[\u201c\u201d]/g, '"'); // curly double quotes
  return [...normalized]
    .map((ch) => {
      if (ch.charCodeAt(0) <= 0xff || WINANSI_HIGH.includes(ch)) return ch;
      if (TRANSLITERATE[ch]) return TRANSLITERATE[ch];
      // Fold accented Latin-Extended to base (r-caron -> r); drop the rest.
      return [...ch.normalize("NFKD").replace(/[\u0300-\u036f]/g, "")]
        .filter((c) => c.charCodeAt(0) <= 0xff || WINANSI_HIGH.includes(c))
        .join("");
    })
    .join("");
}
